import type { MutationHookOptions } from '@apollo/client'
import {
  CreateTrainingsReminderDocument,
  CreateTrainingsReminderMutation,
  CreateTrainingsReminderMutationVariables,
  DestroyReminderDocument,
  DestroyReminderMutationVariables,
  SessionRemindersDocument,
  SessionRemindersQuery,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

export type Reminder = NonNullable<
  NonNullable<NonNullable<SessionRemindersQuery['trainings']>['remindersConnection']>['nodes'][0]
>

export const useLoadReminders = (sessionId: string, ids?: string[]) => {
  const { loading, data } = useQuery(SessionRemindersDocument, {
    variables: { sessionId, ids },
    // If we don't have sessionId, we are creating a session,
    // so we don't have to load the reminders from the backend.
    skip: !sessionId,
  })

  return {
    loading,
    reminders: data ? data.trainings.remindersConnection.nodes : undefined,
    awaitRefetchQueries: true,
    refetchQueries: [SessionRemindersDocument],
  }
}

type Options = MutationHookOptions<
  CreateTrainingsReminderMutation,
  CreateTrainingsReminderMutationVariables
>

export const useCreateReminder = (options?: Options) => {
  const [createTrainingReminder, { loading }] = useMutation(CreateTrainingsReminderDocument, {
    ...options,
    extractorFn: (data) => data.trainingsMutations.createTrainingsReminder,
  })

  return {
    loading,
    create(variables: CreateTrainingsReminderMutationVariables) {
      return createTrainingReminder({
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [SessionRemindersDocument],
      })
    },
  }
}

export const useDestroyReminder = () => {
  const [destroyReminder, { loading }] = useMutation(DestroyReminderDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsReminder,
  })

  return {
    loading,
    destroyReminder(variables: DestroyReminderMutationVariables) {
      return destroyReminder({
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [SessionRemindersDocument],
      })
    },
  }
}
